import { Box, TextField } from "@mui/material";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { useMutation } from "@tanstack/react-query";
import BasicModal from "@/modules/common/components/modals/basic-modal";
import ModalHeader from "@/modules/common/components/modals/modal-header";
import { queryClient } from "@/queryClient";
import { useContext, useState } from "react";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { ListingsActionsMenu } from "@/modules/lead-page/components/lead-listings/listings-actions-menu";
import { ListingImages } from "@/modules/lead-page/components/lead-listings/listing-images";
import { ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { EditListingModal } from "./edit-listing";

interface ListingsActionsProps {
  unitPrice?: number;
  downpayment?: number;
  remainingInstallments?: number;
  maintenance?: number;
  listingId: string;
  compoundId?: number | string;
  websiteListingId?: number | string | null;
  trackUid?: string;
  ownersListingId: number | string;
  trackTypeId?: number;
  numberOfBedrooms?: number;
  numberOfBathrooms?: number;
  unitArea?: number;
  floorNumber?: number;
  finishing?: string;
  listingUid?: string;
}

const ListingsActions = ({
  listingId,
  unitPrice,
  downpayment,
  remainingInstallments,
  maintenance,
  compoundId,
  websiteListingId,
  trackUid,
  trackTypeId,
  numberOfBedrooms,
  numberOfBathrooms,
  unitArea,
  floorNumber,
  finishing,
  listingUid,
}: ListingsActionsProps) => {
  const { lead } = useContext(LeadPageContext);
  const isPersonalLead =
    (lead?.data?.lead?.methodOfContact as { personal?: boolean } | undefined)
      ?.personal === true;

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openCarousel, setOpenCarousel] = useState(false);
  const [openMoveModal, setOpenMoveModal] = useState(false);
  const [targetLeadNanoId, setTargetLeadNanoId] = useState("");
  const [isFetchingJourney, setIsFetchingJourney] = useState(false);

  // TODO HANDLE MOVE PROPERTY LATER — kept as-is from prior implementation.
  const handleMoveListing = useMutation(
    async () => {
      setIsFetchingJourney(true);
      try {
        // return await moveListingV2({ trackUid: trackUid!, journeyUId });
      } catch (error) {
        setIsFetchingJourney(false);
        throw error;
      } finally {
        setIsFetchingJourney(false);
      }
    },
    {
      onSuccess() {
        setTimeout(() => {
          queryClient.invalidateQueries(["getListings"]);
          queryClient.invalidateQueries(["getLeadListings"]);
          queryClient.invalidateQueries(["getLeadTracks"]);
          queryClient.invalidateQueries(["listings"]);
          queryClient.invalidateQueries(["track"]);
          queryClient.invalidateQueries(["getTracksForJourney"]);
        }, 500);

        showToast({
          type: ToastType.SUCCESS,
          message: "Listing Moved Successfully",
          toastId: "successlistingmove",
          options: { position: "bottom-left", autoClose: 3000 },
        });

        setOpenMoveModal(false);
        setTargetLeadNanoId("");
      },
      onError() {
        showToast({
          type: ToastType.ERROR,
          message: "Failed To Move Listing",
          toastId: "errorlistingmove",
          options: { position: "bottom-left", autoClose: 3000 },
        });
      },
    }
  );

  return (
    <>
      <ListingsActionsMenu
        trackUid={trackUid ?? ""}
        websiteListingId={websiteListingId ?? null}
        compoundId={compoundId ?? ""}
        setOpenCarousel={setOpenCarousel}
        setOpenEditModal={setOpenEditModal}
        setOpenMoveModal={setOpenMoveModal}
        trackTypeId={trackTypeId}
      />

      <EditListingModal
        open={openEditModal}
        setOpen={setOpenEditModal}
        listing={{
          unitPrice,
          downpayment,
          remainingInstallments,
          maintenance,
          numberOfBedrooms,
          numberOfBathrooms,
          unitArea,
          floorNumber,
          finishing,
        }}
        context={{
          listingId,
          listingUid,
          trackUid,
        }}
        isPersonalLead={isPersonalLead}
      />

      {/* Move Listing Modal */}
      <EditFieldModal
        open={openMoveModal}
        setOpen={setOpenMoveModal}
        width={500}
        header="Move Listing"
        confirmText="Move"
        isLoading={handleMoveListing.isLoading || isFetchingJourney}
        handleConfirm={() => handleMoveListing.mutate()}
        disabledConfirmButton={
          !targetLeadNanoId || handleMoveListing.isLoading || isFetchingJourney
        }
      >
        <TextField
          autoFocus
          label="Lead Nano ID"
          type="text"
          fullWidth
          variant="outlined"
          value={targetLeadNanoId}
          onChange={(e) => setTargetLeadNanoId(e.target.value)}
          placeholder="Enter lead nano ID (e.g., 500235-fdho)"
          helperText={isFetchingJourney ? "Fetching journey..." : ""}
        />
      </EditFieldModal>

      <BasicModal
        open={openCarousel}
        setOpen={setOpenCarousel}
        width={600}
        fullScreenMobile
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            minHeight: "100%",
            width: "100%",
            padding: "32px",
          }}
        >
          <ModalHeader
            headerText="Listing images"
            handleClose={() => setOpenCarousel(false)}
          />
          <ListingImages trackUid={trackUid} />
        </Box>
      </BasicModal>
    </>
  );
};
export default ListingsActions;
